import { Injectable, inject } from '@angular/core';

import { AppConfigService } from '../config/app-config.service';

import { ErrorReporter, type ErrorContext } from './error-reporter';

/**
 * Adapter por defecto del `ErrorReporter`: stub gateado por DSN de runtime.
 *
 * - SIN `observability.sentryDsn` en `config.json` → NO reporta (el
 *   `GlobalErrorHandler` ya logueó el error localmente; no duplicamos ni
 *   levantamos infra externa que nadie configuró).
 * - CON DSN → emite un registro estructurado que hace de placeholder de
 *   `Sentry.captureException`. Cambiar el stub por el SDK real es reemplazar el
 *   cuerpo de `report()` (y agregar el `Sentry.init({ dsn })` en un initializer).
 *
 * Deliberadamente NO importa `@sentry/angular`: mantener la dependencia opcional
 * fuera del bundle hasta que el DSN exista es parte del gating.
 */
@Injectable()
export class LoggingErrorReporter extends ErrorReporter {
  private readonly appConfig = inject(AppConfigService);

  report(error: unknown, context: ErrorContext): void {
    const dsn = this.dsn();
    if (!dsn) return;

    // STUB listo para @sentry/angular. Reemplazar por:
    //   Sentry.captureException(error, {
    //     tags: { route: context.route, tenantId: context.tenantId },
    //     user: context.userId ? { id: context.userId } : undefined,
    //   });
    // eslint-disable-next-line no-console
    console.error('[ErrorReporter] captura enviada al sink de errores (stub Sentry)', {
      route: context.route,
      userId: context.userId,
      tenantId: context.tenantId,
      error: this.describe(error),
    });
  }

  /** DSN de runtime, o `undefined` si no está configurado o la config aún no cargó. */
  private dsn(): string | undefined {
    if (!this.appConfig.isLoaded()) return undefined;
    return this.appConfig.config().observability.sentryDsn;
  }

  private describe(error: unknown): string {
    if (error instanceof Error) return `${error.name}: ${error.message}`;
    return String(error);
  }
}
